/**
 * ColoringPrint - Figma-matching print renderer for coloring quizzes
 * Layout: PrintQuizTitle → image grid (images ARE the answers)
 */

import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { ColoringQuizData } from '../../../types/legacyQuiz'
import { PrintQuizTitle, PrintQuizProblem, PrintQuizImage, getStyles, getImageUrl, getPrintScale } from './print-common'

interface PrintQuizProps {
  quizId: string | number
  quiz: ColoringQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function ColoringPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const digits = quiz.digits || 0
  const rows = quiz.rows || 1
  const inverted = quiz.inverted ?? false
  const itemsPerRow = Math.ceil(digits / rows)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)
  const offImageUrl = getImageUrl(quiz.coloringOffImage)

  // Build row indices
  const rowIndices: number[][] = []
  for (let r = 0; r < rows; r++) {
    const start = r * itemsPerRow
    const end = Math.min(start + itemsPerRow, digits)
    const indices: number[] = []
    for (let i = start; i < end; i++) indices.push(i)
    rowIndices.push(indices)
  }

  // Correct answer shading
  const correctAnswerArray: number[] | null = (() => {
    if (!showCorrectAnswers || !correctAnswers?.[0]) return null
    const correctStr = correctAnswers[0]
    if (correctStr.length === digits && /^[01]+$/.test(correctStr)) {
      return correctStr.split('').map(Number)
    }
    const count = parseInt(correctStr, 10)
    if (!isNaN(count)) {
      const arr = Array(digits).fill(inverted ? 1 : 0)
      for (let i = 0; i < Math.min(count, digits); i++) arr[i] = inverted ? 0 : 1
      return arr
    }
    return null
  })()

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Problem body (with {image=N} → additionalImages) */}
      <PrintQuizProblem
        problem={quiz.problem}
        quizId={quizId}
        additionalImages={quiz.additionalImages}
        compact={compact}
        stripBlanks
      />

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Grid of items */}
      <div className="flex flex-col items-center gap-[4px] w-full">
        {rowIndices.map((rowItems, rowIndex) => (
          <div key={rowIndex} className="flex items-center justify-center gap-[4px]">
            {rowItems.map((itemIndex) => {
              const isShaded = correctAnswerArray ? correctAnswerArray[itemIndex] === 1 : false
              return offImageUrl ? (
                <div
                  key={itemIndex}
                  className={cn(
                    'w-[56px] h-[56px] flex items-center justify-center rounded-[4px] border border-transparent',
                    isShaded && 'border-[#e50000] bg-[#e50000]/10'
                  )}
                >
                  <img src={offImageUrl} alt={`item-${itemIndex}`} className="w-[48px] h-[48px] object-contain" />
                </div>
              ) : (
                <div
                  key={itemIndex}
                  className={cn(
                    'w-[40px] h-[40px] border border-gray-400',
                    isShaded ? 'border-[#e50000] bg-[#e50000]' : 'bg-white'
                  )}
                />
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
